import { Field, Form, FormikProps } from 'formik';
import { List, ListItemButton } from '@mui/material';
import { LocationType } from '@api/Trinium/Endpoints/Location/types';
import { FormikButtonDropdown, PopupContentProps } from '@forms/Components/Dropdowns/ButtonDropdown';
import { FormikCheckboxInput } from '@forms/Components/CheckInput';
import ContactsDataGrid from '@components/DataGrid/Contacts/Grid';
import { FormikPhoneInput } from '@forms/Components/PhoneMaskInput';
import { FormikReadonlyInput } from '@forms/Components/ReadonlyInput';
import { FormikTextInput } from '@forms/Components/TextInput';
import { OperatingHoursSubform } from './OperatingHoursSubForm';
import { ConsigneeFormik } from './types';
import CityDropdownField from '@forms/Fields/CityDropdown';

import styles from './Consignee.module.css';

type ConsigneeFormProps = FormikProps<ConsigneeFormik> & {
  id: string;
  consigneeId?: string | null;
  readonly?: boolean;
};

export default function ConsigneeForm({ id, consigneeId, values, readonly }: ConsigneeFormProps) {
  const canFullyEdit = !readonly && (!values.id || values.isTrmUnique);

  return (
    <Form
      id={id}
      className={styles.form}>
      {canFullyEdit ? (
        <ConsigneeFullModify values={values} />
      ) : (
        <ConsigneeEditModify
          values={values}
          readonly={readonly}
        />
      )}

      <div className={styles['operating-subform']}>
        <Field
          name='shippingHours'
          component={OperatingHoursSubform}
          label='Shipping Hours'
          readonly={readonly}
        />

        <Field
          name='deliveryHours'
          component={OperatingHoursSubform}
          label='Delivery Hours'
          readonly={readonly}
        />

        <div className={styles['pudl-notes']}>
          <Field
            name='usePuNotes'
            component={FormikCheckboxInput}
            label='Use PU Notes'
            labelPlacement='end'
          />
          <Field
            name='puNotes'
            component={FormikTextInput}
            placeholder='Enter Pickup Notes here...'
            className={styles['pudl-notes-text-input']}
          />
        </div>

        <div className={styles['pudl-notes']}>
          <Field
            name='useDelvNotes'
            component={FormikCheckboxInput}
            label='Use DL Notes'
            labelPlacement='end'
          />
          <Field
            name='delvNotes'
            component={FormikTextInput}
            placeholder='Enter Delivery Notes here...'
            className={styles['pudl-notes-text-input']}
          />
        </div>

        {consigneeId && (
          <div className={styles['contacts']}>
            <div className={styles['data-grid']}>
              <ContactsDataGrid
                consigneeId={consigneeId}
                contacts={values.contact}
              />
            </div>
          </div>
        )}
      </div>
    </Form>
  );
}

/**
 *    When adding a new Location all fields are available (Name, Addresses, etc.)
 */
function ConsigneeFullModify({ values }: { values: ConsigneeFormik }) {
  return (
    <div className={styles['flex-col']}>
      <div className={styles['flex-row']}>
        <Field
          name='name'
          label='Name'
          component={FormikTextInput}
          className={styles['loc-name']}
        />

        <Field
          name='type'
          className={styles['loc-type-btn']}
          component={FormikButtonDropdown({
            ButtonContent: ({ locType }: { locType?: LocationType }) => {
              return <span>{locType ?? 'Select a Type'}</span>;
            },
            buttonContentProps: {
              locType: values.type
            },
            PopupContent: ({ onChange }: PopupContentProps) => {
              return (
                <List>
                  {Object.keys(LocationType)
                    .filter((x) => isNaN(parseInt(x)))
                    .map((key) => (
                      <ListItemButton
                        key={key}
                        onClick={() => {
                          onChange(key);
                        }}>
                        {key}
                      </ListItemButton>
                    ))}
                </List>
              );
            }
          })}
        />
      </div>

      <Field
        name='address1'
        label='Address 1'
        component={FormikTextInput}
      />

      <Field
        name='address2'
        label='Address 2'
        component={FormikTextInput}
      />

      <div className={styles['flex-row']}>
        <CityDropdownField
          fieldName='city'
          className={styles['loc-city-btn']}
        />

        <div className={styles['flex-input']}>
          <span className={styles['label']}>State</span>
          <span className={styles['loc-state']}>{values.city?.state ?? '-'}</span>
        </div>

        <Field
          name='zip'
          label='Zip'
          className={styles['loc-zip']}
          component={FormikTextInput}
        />
      </div>

      <div className={styles['flex-row']}>
        <Field
          name='country'
          label='Country'
          className={styles['loc-country-btn']}
          component={FormikButtonDropdown({
            ButtonContent: ({ country }: { country?: string }) => {
              return <span>{country ?? 'Select a Country'}</span>;
            },
            buttonContentProps: {
              country: values.country?.name
            },
            PopupContent: ({ onChange }: PopupContentProps) => {
              return (
                <List>
                  {[
                    {
                      id: 'US',
                      name: 'United States'
                    },
                    {
                      id: 'MX',
                      name: 'Mexico'
                    },
                    {
                      id: 'CA',
                      name: 'Canada'
                    }
                  ].map((key) => (
                    <ListItemButton
                      key={key.id}
                      onClick={() => {
                        onChange(key);
                      }}>
                      {key.name}
                    </ListItemButton>
                  ))}
                </List>
              );
            }
          })}
        />

        <div className={styles['flex-input']}>
          <span className={styles['label']}>Area</span>
          <span className={styles['loc-area']}>{!values.area ? '-' : values.area}</span>
        </div>
      </div>

      <div className={styles['flex-row']}>
        <Field
          name='phone'
          label='Phone'
          component={FormikPhoneInput}
          className={styles.phone}
        />

        <Field
          name='fax'
          label='Fax'
          component={FormikPhoneInput}
          className={styles.phone}
        />
      </div>

      <Field
        type='email'
        name='email'
        label='Email'
        component={FormikTextInput}
      />

      <Field
        name='notes'
        label='Notes'
        component={FormikTextInput}
        placeholder='Enter Location Notes here...'
        textArea
      />
    </div>
  );
}

/**
 *	When editing a location, there are limited available fields to change. Some users can't edit at all.
 */
function ConsigneeEditModify({ values, readonly }: { values: ConsigneeFormik; readonly?: boolean }) {
  return (
    <div className={styles['flex-col']}>
      <div className={styles['flex-row']}>
        <Field
          name='name'
          label='Name'
          component={FormikReadonlyInput}
          className={styles['loc-name']}
        />

        <Field
          name='type'
          label='Type'
          className={styles['loc-type-btn']}
          component={FormikReadonlyInput}
        />
      </div>

      <Field
        name='address1'
        label='Address 1'
        component={FormikReadonlyInput}
      />

      <Field
        name='address2'
        label='Address 2'
        component={FormikReadonlyInput}
      />

      <div className={styles['flex-row']}>
        <Field
          name='city.name'
          label='City'
          className={styles['loc-city-btn']}
          component={FormikReadonlyInput}
        />

        <div className={styles['flex-input']}>
          <span className={styles['label']}>State</span>
          <span className={styles['loc-state']}>{values.city?.state ?? '-'}</span>
        </div>

        <Field
          name='zip'
          label='Zip'
          className={styles['loc-zip-readonly']}
          component={FormikReadonlyInput}
        />
      </div>

      <div className={styles['flex-row']}>
        <Field
          name='country.name'
          label='Country'
          className={styles['loc-country-btn']}
          component={FormikReadonlyInput}
        />

        <div className={styles['flex-input']}>
          <span className={styles['label']}>Area</span>
          <span className={styles['loc-area']}>{values.area ?? '-'}</span>
        </div>
      </div>

      <div className={styles['flex-row']}>
        <Field
          name='phone'
          label='Phone'
          className={styles.phone}
          component={FormikReadonlyInput}
          asPhoneLink
        />

        <Field
          name='fax'
          label='Fax'
          component={FormikReadonlyInput}
          asPhoneLink
          className={styles.fax}
        />
      </div>

      <Field
        name='email'
        label='Email'
        component={FormikReadonlyInput}
      />

      <Field
        name='notes'
        label='Notes'
        component={!readonly ? FormikTextInput : FormikReadonlyInput}
        placeholder='Enter Location Notes here...'
        textArea
      />
    </div>
  );
}
